import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import styles from '@/storybook/components/FormCollection/Input.module.css';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import DropdownPlaceholderWithBadge from '@/storybook/components/FormCollection/components/DropdownPlaceholderWithBadge/DropdownPlaceholderWithBadge';
import DropdownRatingPanel from '@/storybook/components/FormCollection/components/DropdownRatingPanel/DropdownRatingPanel';
import DropdownValueWithRatingStars from '@/storybook/components/FormCollection/components/DropdownValueWithRatingStars/DropdownValueWithRatingStars';
import VirtualizedRows from '@/storybook/components/FormCollection/components/VirtualizedRows/VirtualizedRows';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import cx from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultiLevelSelectOptions } from './hooks/useMultiLevelSelectOptions';
import { useMultiLevelSelectVirtualization } from './hooks/useMultiLevelSelectVirtualization';

export type { GroupedOptionList } from './hooks/useMultiLevelSelectOptions';

export type MultiLevelSelectProps<T = string> = {
  'label'?: ReactNode;
  'tooltip'?: ReactNode;
  'options': SelectItem<T>[];
  'unfilteredOptions'?: SelectItem<T>[];
  'isLoading'?: boolean;
  'value': SelectItem<T>[];
  'onChange': (selectedValue: SelectItem<T>[]) => void;
  'placeholder'?: string;
  'disabled'?: boolean;
  'className'?: string;
  'clearable'?: boolean;
  'searchable'?: boolean;
  'adobeEventTrackFunctions'?: () => void;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownLabelTestId'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'size'?: keyof typeof DropdownButtonSize;
};

export default function MultiLevelSelect<T>({
  label,
  tooltip,
  options = [],
  unfilteredOptions,
  isLoading,
  value = [],
  onChange,
  placeholder,
  disabled = false,
  className,
  clearable,
  searchable,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'multiLevelSelect',
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  size = 'L',
}: MultiLevelSelectProps<T>) {
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [selectedOption, setSelectedOption] = useState<SelectItem<T> | undefined>(undefined);
  const [memoizedValueList, setMemoizedValueList] = useState<Array<SelectItem<T>>>([]);
  const [isForceTruncate, setForceTruncate] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const fixedOptions = useMemo(() => {
    if (options == null) return [];
    return options.filter(opt => opt.value != null && opt.label != null);
  }, [options]);

  const fixedOptionsLength = fixedOptions?.length ?? 0;

  const valueListContainerRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  const dropdownRef = useOnClickOutside(() => {
    if (isOpen) onDropdownToggle?.(false);
    setIsOpen(false);
  });

  useScrollIntoView({
    elementRef: dropdownRef,
    isVisible: isOpen,
  });

  const {
    searchResults,
    groupedOptions,
    isOptionSelected,
    handleClickOnCheckbox,
    categoriesToDisplay,
    dropdownButtonValue,
    handleSelectLevel,
  } = useMultiLevelSelectOptions<T>({
    options,
    unfilteredOptions,
    fixedOptions,
    value,
    memoizedValueList,
    searchInput,
    selectedOption,
    onChange,
    setSelectedOption,
  });

  const { virtualizedList, parentRef, renderVirtualRow } = useMultiLevelSelectVirtualization<T>({
    selectedOptions: groupedOptions.selected,
    notSelectedOptions: groupedOptions.notSelected,
    categoriesToDisplay,
    noCategoryLabel: t('components.dropdownPanel.noCategory'),
    value,
    isOptionSelected,
    handleClickOnCheckbox,
    setSelectedOption,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setMemoizedValueList(value);
      setSearchInput(undefined);
      setSelectedOption(undefined);
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, value, onDropdownToggle]);

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    onChange([]);
  }, [disabled, clearable, onChange]);

  useEffect(() => {
    if (!value.length) return setForceTruncate(false);
    if (!dropdownButtonRef.current || !valueListContainerRef.current) return setForceTruncate(false);
    const dropdownButtonRect = dropdownButtonRef.current.getBoundingClientRect();
    const valueListRect = valueListContainerRef.current.getBoundingClientRect();
    const CTAsContainerWidth = clearable ? 75 : 45;
    setForceTruncate(dropdownButtonRect.width - CTAsContainerWidth < valueListRect.width);
  }, [value, dropdownButtonRef, valueListContainerRef, clearable]);

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={tooltip} data-testid={dropdownLabelTestId} />

      <section className={styles.dropdownContainer}>
        <DropdownButton
          ref={dropdownButtonRef}
          size={size}
          type="multiLevelSelect"
          placeholder={
            <DropdownPlaceholderWithBadge
              label={t('components.multiLevelDropdown.emptyListPlaceholder', { placeholder })}
            />
          }
          numOfValue={value.length}
          active={isOpen}
          onClick={handleToggleDropdown}
          onClear={handleOnClearValue}
          clearable={clearable}
          disabled={disabled}
          data-testid={dropdownButtonTestId}
          forceTruncate={isForceTruncate}
        >
          {dropdownButtonValue.length > 0 ? (
            <DropdownValueWithRatingStars value={dropdownButtonValue} ref={valueListContainerRef} />
          ) : (
            ''
          )}
        </DropdownButton>

        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          isSearchable={!selectedOption ? searchable : undefined}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchResults.length}
          data-testid={dropdownListTestId}
          optionsCount={fixedOptionsLength}
          listRef={parentRef}
        >
          {selectedOption ? (
            <DropdownRatingPanel
              label={selectedOption.label}
              onClick={handleSelectLevel}
              onClose={() => setSelectedOption(undefined)}
              data-testid="multiLevelSelectRatingPanel"
            />
          ) : (
            <VirtualizedRows virtualization={virtualizedList} renderRow={renderVirtualRow} />
          )}
        </DropdownList>
      </section>
    </div>
  );
}
